import { ReactNode, useState } from "react";
import { Link, useLocation } from "wouter";
import { LayoutDashboard, Users, FileText, Briefcase, Settings, LogOut, MessageSquare, Star, Menu, X, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { Logo } from "@/components/common/Logo";

interface AdminLayoutProps {
  children: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const [location, setLocation] = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const navItems = [
    { href: "/ab-hq", label: "Dashboard", icon: LayoutDashboard },
    { href: "/ab-hq/leads", label: "Leads CRM", icon: Users },
    { href: "/ab-hq/projects", label: "Projects", icon: Briefcase },
    { href: "/ab-hq/services", label: "Services", icon: Settings },
    { href: "/ab-hq/blogs", label: "Blog Posts", icon: FileText },
    { href: "/ab-hq/testimonials", label: "Testimonials", icon: Star },
  ];

  const handleLogout = () => {
    localStorage.removeItem("admin_authenticated");
    setLocation("/ab-hq/login");
  };

  return (
    <div className="flex min-h-screen bg-muted/20">
      {/* Sidebar */}
      <aside
        className={`flex flex-col bg-card border-r border-border/50 transition-all duration-300 ${
          sidebarOpen ? "w-64" : "w-16"
        }`}
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-3 border-b border-border/50 overflow-hidden">
          {sidebarOpen && (
            <Link href="/" className="flex-1 overflow-hidden">
              <Logo className="h-8 w-auto" showTagline={false} />
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={() => setSidebarOpen((prev) => !prev)}
          >
            {sidebarOpen ? <ChevronLeft className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 py-6 px-2 space-y-1">
          {navItems.map((item) => {
            const isActive =
              location === item.href ||
              (item.href !== "/ab-hq" && location.startsWith(item.href));
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
                title={!sidebarOpen ? item.label : undefined}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-2 border-t border-border/50">
          <Button
            variant="ghost"
            className={`w-full text-muted-foreground hover:text-foreground ${
              sidebarOpen ? "justify-start px-3" : "justify-center px-0"
            }`}
            onClick={handleLogout}
            title={!sidebarOpen ? "Logout" : undefined}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {sidebarOpen && <span className="ml-2">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto min-w-0">
        <div className="h-16 flex items-center justify-between px-6 border-b border-border/50 bg-card/50 backdrop-blur">
          <span className="text-sm text-muted-foreground font-medium">Admin Panel</span>
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">Admin User</span>
            <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">
              A
            </div>
          </div>
        </div>
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}
